// Per-screen contextual sidebar slot.
//
// The redesigned AppShell renders a fixed NAVIGATE nav rail, then a slot below
// it that each page fills with its own contextual block (filter groups / recent
// list / type legend + a bottom action button). Pages publish their block via
// `useSidebarSlot(node)`; the shell renders the current node via
// `<SidebarSlotOutlet/>`. This avoids prop-drilling through the router.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface SidebarSlotApi {
  node: ReactNode;
  setNode: (node: ReactNode) => void;
}

const SidebarSlotContext = createContext<SidebarSlotApi | null>(null);

/** Wraps the shell so descendant pages can publish a contextual sidebar block. */
export function SidebarSlotProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode>(null);
  const value = useMemo<SidebarSlotApi>(() => ({ node, setNode }), [node]);
  return <SidebarSlotContext.Provider value={value}>{children}</SidebarSlotContext.Provider>;
}

/**
 * Publish a contextual sidebar block for the lifetime of the calling component.
 * Clears the slot on unmount. Pass the same `node` reference (or memoize it) to
 * avoid redundant re-publishes. Safe to call when no provider is mounted (no-op).
 */
export function useSidebarSlot(node: ReactNode): void {
  const ctx = useContext(SidebarSlotContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setNode(node);
    return () => ctx.setNode(null);
    // ctx.setNode is stable (state setter); re-run only when the node changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);
}

/** Renders the current contextual block. Placed under the nav rail by AppShell. */
export function SidebarSlotOutlet() {
  const ctx = useContext(SidebarSlotContext);
  return <>{ctx?.node ?? null}</>;
}
